import {computed, IDisposer, useLocalStore, useEffect, useAsObservableSource, observer, React, computedFn} from '@api'
import {DashStyle, DrawShape, GHOSTED_OPACITY, TldrawApp, useTldrawApp, useWhiteboardStore, WhiteboardStore} from '@modules/whiteboard'
import {SVGContainer, TLComponentProps, Utils} from '@tldraw/core'
import Vec from '@tldraw/vec'
import * as pf from 'perfect-freehand'
import {DrawUtil} from '.'
import {ShapeComponentStore} from '../'
/// region Store

export class DrawComponentStore extends ShapeComponentStore<DrawShape, DrawUtil> {
  constructor(public props: MyProps & { app: TldrawApp }) {
    super(props)
  }

  @computed get boundsFromPoints() {
    return Utils.getBoundsFromPoints(this.shape.points)
  }

  @computed get bounds() {
    const {boundsFromPoints, shape: {point}} = this
    return Utils.translateBounds(boundsFromPoints, point)
  }

  get shape() { return this.props.shape}

  @computed get drawStrokePoints() {
    const {shape: {points}, freehandOptions} = this
    return pf.getStrokePoints(points, freehandOptions)
  }

  @computed get outlineStrokePoints() {
    const {drawStrokePoints, freehandOptions} = this
    return pf.getStrokeOutlinePoints(drawStrokePoints, freehandOptions)
  }

  @computed get freehandOptions() {
    const {shape, app: {getFreehandOptions}} = this
    return getFreehandOptions(shape)
  }
  /**
   * Get path data for a stroke with the DashStyle.Draw dash style.
   */
  @computed get drawStrokePathTDSnapshot() {
    const {shape} = this, closed = true
    if (shape.points.length < 2) return ''

    const {outlineStrokePoints: points} = this

    if (!points.length) {
      return ''
    }

    const max = points.length - 1

    return points
      .reduce(
        (acc, point, i, arr) => {
          if (i === max) {
            if (closed) {
              acc.push('Z')
            }
          } else {
            acc.push(point, Vec.med(point, arr[i + 1]))
          }
          return acc
        },
        ['M', points[0], 'Q']
      )
      .join(' ')
      .replaceAll(Utils.TRIM_NUMBERS, '$1')
  }


  /**
   * Get SVG path data for a shape that has a DashStyle other than DashStyles.Draw.
   */
  @computed get solidPath() {
    const {shape: {points}} = this

    if (points.length < 2) return 'M 0 0 L 0 0'

    const {drawStrokePoints} = this,
      strokePoints = drawStrokePoints.map((pt) => pt.point.slice(0, 2))

    const path = Utils.getSvgPathFromStroke(strokePoints, false)

    return path
  }

  @computed get polygonPath() {
    const {shape: {points}, freehandOptions} = this
    if (points.length < 2) return ''

    return Utils.getSvgPathFromStroke(
      pf.getStrokePoints(points, freehandOptions).map((pt) => pt.point)
    )
  }

  @computed get path() {
    const {shape: {style}} = this
    return style.dash === DashStyle.Draw
           ? this.drawStrokePathTDSnapshot
           : this.solidPath
  }


  @computed get verySmall() {
    const {bounds, shapeStyle: {strokeWidth}} = this
    return bounds.width <= strokeWidth / 2 && bounds.height <= strokeWidth / 2
  }

  @computed get shouldFill() {
    const {shape: {style: {strokeWidth, isFilled}, points}} = this

    return isFilled && points.length > 3 && Vec.dist(points[0], points[points.length - 1]) < strokeWidth * 2
  }

  @computed get strokeDash() {
    const {shapeStyle: {dash, strokeWidth}} = this

    switch (dash) {
      case DashStyle.Draw:
      case DashStyle.Solid:
        return 'none'

      case DashStyle.Dotted:
        return `0.1 ${strokeWidth * 4}`

      case DashStyle.Dashed:
        return `${strokeWidth * 4} ${strokeWidth * 4}`

      default:
        return 'none'
    }
  }

  @computed get strokeDashOffset() {
    const {shapeStyle: {dash}} = this

    switch (dash) {
      case DashStyle.Draw:
      case DashStyle.Solid:
        return `none`

      case DashStyle.Dotted:
      case DashStyle.Dashed:
        return `0`

      default:
        return 'none'
    }
  }

  @computed get id() {
    const {shape: {id, type}} = this
    return `${id}_${type}_svg`
  }
}

export const DrawComponentStoreContext = React.createContext<DrawComponentStore>(null)
export const useDrawComponentStore = () => React.useContext(DrawComponentStoreContext)

/// endregion

/// region Components

interface MyProps extends TLComponentProps<DrawShape> {

}


export const DrawComponent = observer<MyProps, any>(function DrawComponent(props, ref) {
  const app = useTldrawApp(),
    obsProps = useAsObservableSource({...props, app}),
    store = useLocalStore(() => new DrawComponentStore(obsProps)),
    {props: {events, isGhost, shape}} = store

  useEffect(() => store.registerEffects(), [])

  const {verySmall, shapeStyle: {strokeWidth, strokeColor}, id} = store

  // For very short lines, draw a point instead of a line

  if (verySmall) {
    const sw = 1 + strokeWidth

    return (
      <SVGContainer ref={ref} id={id} {...events}>
        <circle
          r={sw}
          fill={strokeColor}
          stroke={strokeColor}
          pointerEvents='all'
          opacity={isGhost ? GHOSTED_OPACITY : 1}
        />
      </SVGContainer>
    )
  }

  if (shape.style.dash === DashStyle.Draw) {
    const {shouldFill, shapeStyle: {backgroundColor, strokeColor}, polygonPath, path} = store
    return (
      <SVGContainer ref={ref} id={id} {...events}>
        <g opacity={isGhost ? GHOSTED_OPACITY : 1}>
          {shouldFill && (
            <path
              d={polygonPath}
              stroke='none'
              fill={backgroundColor}
              strokeLinejoin='round'
              strokeLinecap='round'
              pointerEvents='fill'
            />
          )}
          <path
            d={path}
            fill={strokeColor}
            stroke={strokeColor}
            strokeWidth={strokeWidth / 2}
            strokeLinejoin='round'
            strokeLinecap='round'
            pointerEvents='all'
          />
        </g>
      </SVGContainer>
    )
  }

  // For solid, dash and dotted lines, draw a regular stroke path

  const {path, shouldFill, shapeStyle: {backgroundColor}, strokeDash, strokeDashOffset} = store
  const sw = 1 + strokeWidth * 1.5

  return (
    <SVGContainer ref={ref} id={id} {...events}>
      <g opacity={isGhost ? GHOSTED_OPACITY : 1}>
        <path
          d={path}
          fill={shouldFill ? backgroundColor : 'none'}
          stroke='none'
          strokeWidth={Math.min(4, strokeWidth * 2)}
          strokeLinejoin='round'
          strokeLinecap='round'
          pointerEvents={shouldFill ? 'all' : 'stroke'}
        />
        <path
          d={path}
          fill='none'
          stroke={strokeColor}
          strokeWidth={sw}
          strokeDasharray={strokeDash}
          strokeDashoffset={strokeDashOffset}
          strokeLinejoin='round'
          strokeLinecap='round'
          pointerEvents='stroke'
        />
      </g>
    </SVGContainer>
  )
}, {forwardRef: true})


// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/draw/DrawComponent.tsx